<template>
  <div class="joule-law-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Q = I²Rt$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-heat"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="coolGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#38bdf8" />
                <stop offset="100%" stop-color="#1e3a8a" />
             </linearGradient>
          </defs>

          <!-- The Circuit wires -->
          <line x1="20" y1="120" x2="140" y2="120" stroke="#475569" stroke-width="4" stroke-linecap="round" />
          <line x1="260" y1="120" x2="380" y2="120" stroke="#475569" stroke-width="4" stroke-linecap="round" />
          
          <!-- Moving Charge Carriers (High current -> faster moving spheres) -->
          <g v-if="current > 0">
             <circle v-for="c in charges" :key="c.id" r="3" fill="#a3e635" filter="url(#glow-heat)">
                <animate v-if="c.side === 'left'" attributeName="cx" from="20" to="140" :dur="`${2/current}s`" repeatCount="indefinite" />
                <animate v-if="c.side === 'right'" attributeName="cx" from="260" to="380" :dur="`${2/current}s`" repeatCount="indefinite" />
                <animate attributeName="cy" values="120;120" dur="1s" repeatCount="indefinite" />
             </circle>
          </g>

          <!-- The Resistor Heater (Zigzag) -->
          <g transform="translate(140, 100)">
             <!-- Base gray line -->
             <path d="M 0,20 L 10,0 L 20,40 L 30,0 L 40,40 L 50,0 L 60,40 L 70,0 L 80,40 L 90,20 L 120,20" 
                   fill="none" stroke="#334155" stroke-width="6" stroke-linejoin="round" />
             <!-- Glowing layer based on current + resistance -->
             <path d="M 0,20 L 10,0 L 20,40 L 30,0 L 40,40 L 50,0 L 60,40 L 70,0 L 80,40 L 90,20 L 120,20" 
                   fill="none" :stroke="heatColor" stroke-width="5" stroke-linejoin="round" filter="url(#glow-heat)" />
             
             <!-- Rising Heat Particles -->
             <g v-if="power > 5">
                <circle v-for="p in particles" :key="p.id" :cx="p.x" :cy="p.y" :r="p.r" :fill="p.c" opacity="0.6" />
             </g>
          </g>

          <!-- Thermometer Tooltip Area -->
          <g transform="translate(350, 40)">
             <rect x="0" y="0" width="12" height="150" fill="rgba(255,255,255,0.05)" rx="6" />
             <rect x="0" :y="150 - tempRatio * 150" width="12" :height="tempRatio * 150" fill="#ef4444" rx="6" filter="url(#glow-heat)" />
             <text x="-40" y="10" fill="#94a3b8" font-size="10" font-weight="bold">宏观热量 T</text>
          </g>

          <text x="20" y="30" fill="#64748b" font-size="10">微观实质：自由电子与导电晶格的动能碰撞转换</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>热功率 P</label>
              <span class="val">{{ power.toFixed(1) }} <small>W</small></span>
           </div>
           <div class="hud-item orange">
              <label>预测热量 Q</label>
              <span class="val">{{ (power * (timeCount/10)).toFixed(0) }} <small>J</small></span>
           </div>
           <div class="hud-item red" v-if="tempRatio > 0.8">
              <label>警报</label>
              <span class="val" style="color:#ef4444;">过载过热</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>电流强度 I (影响I²倍率)</label>
                 <el-slider v-model="current" :min="0" :max="15" :step="0.1" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>电阻阻值 R (Ω)</label>
                 <el-slider v-model="resistance" :min="1" :max="50" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>焦耳定律揭示了电能向内能转化的**平方规律**。当电流 <code>I</code> 翻倍，热功率 <code>P</code> 会翻四倍！由于电阻丝原子对高速奔流的电子存在剧烈的阻碍碰撞，这些撞击能量最终全部转化为了原子的无序热运动。所以，大电阻、大电流是造成电器发烫、电线起火的头号真凶，也是电炉丝、电热水器的工作灵魂。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JouleLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      current: 4,
      resistance: 10,
      temp: 20, // Initial room temp
      timeCount: 0,
      charges: [
         { id:1, side:'left' }, { id:2, side:'left' },
         { id:3, side:'right' }, { id:4, side:'right' }
      ],
      particles: [],
      timer: null
    }
  },
  computed: {
    power() {
       return this.current * this.current * this.resistance;
    },
    tempRatio() {
       // max display around 500W equivalent
       let TR = (this.temp - 20) / 200;
       return Math.min(1.0, Math.max(0, TR));
    },
    heatColor() {
       if (this.current < 0.5) return '#334155';
       // Map to glowing infrared/red
       let b = Math.min(255, this.power * 0.4);
       return `rgb(${Math.min(255, 100 + b)}, ${Math.max(40, 80 - b/4)}, 40)`;
    }
  },
  methods: {
    animate() {
       this.timeCount++;
       // Thermal simulation
       const heatingRate = this.power * 0.08;
       const coolingRate = (this.temp - 20) * 0.05;
       this.temp += (heatingRate - coolingRate) * 0.1;
       if (this.temp < 20) this.temp = 20;

       // Particles
       if (this.power > 10 && Math.random() < this.power/400) {
          this.particles.push({
             id: Math.random(),
             x: 10 + Math.random() * 80,
             y: 20,
             v: 1 + Math.random() * 2,
             r: 2 + Math.random() * 3,
             c: Math.random() < 0.5 ? '#f87171' : '#fb923c'
          });
       }
       for(let i=this.particles.length-1; i>=0; i--) {
          this.particles[i].y -= this.particles[i].v;
          if (this.particles[i].y < -60) this.particles.splice(i, 1);
       }

       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.joule-law-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } &.red { border-left: 3px solid #ef4444; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #fff1f2; padding: 10px 14px; border-radius: 10px; border: 1px solid #ffe4e6; display: flex; align-items: flex-start; gap: 10px; i { color: #f43f5e; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #9f1239; line-height: 1.5; strong { color: #881337; } } }
</style>
